import React, { useState, useEffect } from 'react';
import { AdminDashboard } from './AdminDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    website: ''
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await adminApi.getCustomers();
      setCustomers(data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer.id);
    setFormData({
      name: customer.name,
      logo: customer.logo || '',
      website: customer.website || ''
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingCustomer(null);
    setFormData({
      name: '',
      logo: '',
      website: ''
    });
  };

  const handleCancel = () => {
    setEditingCustomer(null);
    setIsCreating(false);
    setFormData({ name: '', logo: '', website: '' });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const result = await adminApi.uploadCustomerLogo(file);
      setFormData({ ...formData, logo: result.path });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        toast.error('Customer name is required');
        return;
      }

      if (isCreating) {
        await adminApi.createCustomer(formData);
        toast.success('Customer created successfully');
      } else {
        await adminApi.updateCustomer(editingCustomer, formData);
        toast.success('Customer updated successfully');
      }

      loadCustomers();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save customer');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await adminApi.deleteCustomer(customerId);
      toast.success('Customer deleted successfully');
      loadCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  if (loading) {
    return (
      <AdminDashboard>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Loading customers...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Customers</h1>
            <p className="text-slate-600 mt-1">Manage your company customers</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#1E4E9E] to-[#00B050] hover:from-[#00B050] hover:to-[#1E4E9E] text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Customer
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingCustomer) && (
          <Card className="border-2 border-[#1E4E9E]">
            <CardHeader>
              <CardTitle>{isCreating ? 'Create New Customer' : 'Edit Customer'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Customer name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Website URL</label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://customer-website.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Logo</label>
                <div className="flex items-center space-x-4">
                  <label className="cursor-pointer">
                    <div className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#1E4E9E] transition-colors">
                      <Upload className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {uploading ? 'Uploading...' : 'Upload Logo'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {formData.logo && (
                    <div className="flex items-center space-x-2">
                      <ImageIcon className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-600">Logo uploaded</span>
                    </div>
                  )}
                </div>
                {formData.logo && (
                  <img
                    src={`${BACKEND_URL}${formData.logo}`}
                    alt="Logo Preview"
                    className="mt-4 w-32 h-32 object-contain rounded-lg border bg-white p-2"
                  />
                )}
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleSave} className="bg-[#00B050] hover:bg-[#00B050]/90">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customers List */}
        <div className="grid md:grid-cols-3 gap-6">
          {customers.map((customer) => (
            <Card key={customer.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{customer.name}</CardTitle>
                    {customer.website && (
                      <a 
                        href={customer.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#1E4E9E] hover:underline mt-1 block"
                      >
                        {customer.website}
                      </a>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(customer)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(customer.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {customer.logo ? (
                  <div className="flex items-center justify-center h-32 bg-slate-50 rounded-lg">
                    <img
                      src={`${BACKEND_URL}${customer.logo}`}
                      alt={customer.name}
                      className="max-h-24 max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 bg-slate-50 rounded-lg">
                    <p className="text-slate-400 text-sm">No logo</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {customers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">No customers yet. Create your first customer!</p>
          </div>
        )}
      </div>
    </AdminDashboard>
  );
};
